import React, { ReactElement } from "react";
import { Box, Link, Text, Flex, Checkbox } from "@chakra-ui/react";
import { ChakraFAIcon } from "@/components/ui/Icon";

import { isNoCookieYoutubeUrl } from "@/js/utils/urlUtils";
import Tooltip from "rc-tooltip";
import { colours } from "@/utils/themeUtils";
import { useFeatureFlagsQuery } from "@/query/featureFlagsQuery";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { FeatureFlag } from "@/api/classroomFeatureFlags";

export interface Item {
	text: string;
	value: string;
}

export enum ResourceType {
	Url,
	Signature,
}

/** This is not safe to be sent as a DTO as it does not match the API contract. */
export interface ResourceOption {
	label: string;
	/** Indicates whether the `resourceValue` property is a URL or signature. */
	resourceType: ResourceType;
	/** Actual URL or signature. Whether this is a URL or signature is indicated by the `resourceType` property. */
	resourceValue: string;
}

export interface SelectedResource extends ResourceOption {
	openTab: boolean;
	/**
	 * True by defailt. Updated based on server-side validation outcome upon submission.
	 */
	isValid: boolean;

	isLockedFocusCompatible: boolean;

	/**
	 * The ID of the resource. This is only used when updating an existing focus resource.
	 */
	id?: string;
}

interface Props {
	selectedResources: SelectedResource[];
	onRemove: (resource: SelectedResource) => void;
	onCheckboxChanged: (resource: SelectedResource) => void;
	lockedFocus: boolean;
}

export function SelectedFocusResources(props: Props): ReactElement {
	const userSessionStore = useUserSessionStore();
	const featureFlagsQuery = useFeatureFlagsQuery(userSessionStore.active_device as string);

	if (props.selectedResources.length === 0) {
		return <></>;
	}

	const anyResourcesAreInvalid = props.selectedResources.some((resource) => !resource.isValid);

	const anyFocusLockIncompatibleResources = props.selectedResources.some((resource) => !resource.isLockedFocusCompatible);

	return (
		<Box mt={3} data-testid="selected-focus-resources">
			{anyResourcesAreInvalid && (
				<Flex bg={colours.danger.background.weaker} p={3} rounded={"md"} my={3} data-testid="invalid-resource-selected">
					<Box mr={3} ml={1}>
						<ChakraFAIcon icon="fa-lock" variant="solid" color={colours.red[500]} width="0.8em" height="0.8em" />
					</Box>
					<Box flex={1}>
						<Text color={colours.red[500]} fontWeight={"medium"}>
							Locked rule
						</Text>
						<Text>
							Resources with locked rules are not allowed to be added. Please remove the highlighted resource(s) to continue.{" "}
							<Link
								href="https://help.classwize.net/hc/en-gb/articles/6354876577948-Rules-Validation-Overview#h_01HBFRZWRGP0WXNKSP6DGVNDQ2"
								target="_blank"
							>
								Locked rules
								<ChakraFAIcon icon="fa-arrow-up-right-from-square" width="0.8em" height="0.8em" ml={2} />
							</Link>
						</Text>
					</Box>
				</Flex>
			)}

			{props.lockedFocus && anyFocusLockIncompatibleResources && (
				<Flex bg={colours.danger.background.weaker} p={15} rounded={"md"} my={15} data-testid="invalid-resource-selected">
					<Box mr={4}>
						<ChakraFAIcon icon="fa-lock" variant="solid" color={colours.red[500]} width="0.8em" height="0.8em" />
					</Box>
					<Box flex={1}>
						<Text color={colours.red[500]} fontWeight={"medium"}>
							Locked focus
						</Text>
						<Text>Only full path URLs can be locked. Please remove the highlighted resource(s) to continue. </Text>
					</Box>
				</Flex>
			)}

			<Box border={"1px solid #eeeeee"} rounded={"md"} mt={3}>
				<Flex alignItems="center" px={5} py={2} fontWeight={500} shadow={"sm"}>
					<Box flex={1}>Resource</Box>
					<Box mx={5}>
						<Flex alignItems="center" justifyContent="center" gap="8px">
							<Text lineHeight="16px" fontWeight="medium">
								Open tab
							</Text>
							<Tooltip
								placement="topRight"
								trigger="hover"
								destroyTooltipOnHide={true}
								overlayClassName="Tooltip"
								overlayStyle={{ pointerEvents: "auto" }}
								overlay={
									<Text>
										The URL will open in a new tab on the student device.{" "}
										<a
											href="https://help.classwize.net/hc/en-gb/articles/11598018402332"
											target="_blank"
											rel="noopener noreferrer"
											style={{ cursor: "pointer", textDecoration: "underline" }}
										>
											Find out more
										</a>
									</Text>
								}
								// The arrow will not be pointing to the icon if we don't offset it
								align={{ offset: [24, 0] }}
							>
								<ChakraFAIcon
									icon="fa-question-circle"
									color={colours.blueGray[400]}
									data-testid={"question"}
									width="0.8em"
									height="0.8em"
								/>
							</Tooltip>
						</Flex>
					</Box>
					<Box></Box>
				</Flex>
				{props.selectedResources.map((resource, index) => (
					<Flex
						key={index}
						style={{
							backgroundColor:
								!resource.isValid || (props.lockedFocus && !resource.isLockedFocusCompatible)
									? colours.danger.background.weaker
									: "inherit",
						}}
						alignItems="center"
						px={5}
						py={4}
					>
						<Box flex={1}>
							<Text overflowWrap={"break-word"} wordBreak={"break-all"}>
								{resource.label}
							</Text>
						</Box>
						<Flex mx={5} alignItems="center">
							<Flex>
								<Checkbox
									name="alert"
									isChecked={resource.openTab}
									isDisabled={
										isNoCookieYoutubeUrl(resource.resourceValue) ||
										(featureFlagsQuery.data?.includes(FeatureFlag.FOCUS_LOCK_MODE) && props.lockedFocus)
									}
									onChange={() => props.onCheckboxChanged(resource)}
									data-testid={`checkbox-${index}`}
								/>
								{featureFlagsQuery.data?.includes(FeatureFlag.FOCUS_LOCK_MODE) && props.lockedFocus && (
									<Box>
										<ChakraFAIcon
											ml={2}
											icon="fa-lock"
											variant="solid"
											color={colours.blueGray[400]}
											data-testid={"locked-focus-resource-lock-icon"}
											width="0.8em"
											height="0.8em"
										/>
									</Box>
								)}
							</Flex>
							<ChakraFAIcon
								ml={5}
								icon="fa-close"
								color={colours.danger.icon}
								cursor="pointer"
								onClick={() => props.onRemove(resource)}
								data-testid={`remove-${index}`}
							/>
						</Flex>
					</Flex>
				))}
			</Box>
		</Box>
	);
}
